import { flexRender, Row, Column } from '@tanstack/react-table'
import { Virtualizer } from '@tanstack/react-virtual'
import { TableBody, TableCell, TableRow } from '@/components/shadcn/table'
import { motion } from 'motion/react'
import clsx from 'clsx'
import { useDroppable } from '@dnd-kit/core'
import { cn } from '@/lib/utils'

const MotionTableCell = motion(TableCell)
const MotionTableRow = motion(TableRow)

interface DataTableBodyV2Props<TData> {
  tableBodyRef: React.RefObject<HTMLTableSectionElement>
  isRefetching: boolean
  nrOfColumns: number
  rows: Row<TData>[]
  shouldVirtualize: boolean
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>
  frozenColOffsetsById: Record<string, number>
  leafColumns: Column<TData, unknown>[]
  freezePane?: number
  compactMode: boolean
  cellMeasureRefs: React.MutableRefObject<Record<string, HTMLDivElement[]>>
  isRegrouping?: boolean
  activeDragId?: string | null
}

// Simple skeleton component for loading state
function TableBodySkeleton({ nrOfColumns }: { nrOfColumns: number }) {
  return (
    <TableBody>
      {Array.from({ length: 5 }).map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {Array.from({ length: nrOfColumns }).map((_, colIndex) => (
            <TableCell key={colIndex} className="py-4">
              <div className="h-4 w-full animate-pulse rounded bg-muted" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  )
}

export function DataTableBodyV2<TData>({
  tableBodyRef,
  isRefetching,
  nrOfColumns,
  rows,
  shouldVirtualize,
  rowVirtualizer,
  frozenColOffsetsById,
  leafColumns,
  freezePane,
  compactMode,
  cellMeasureRefs,
  isRegrouping = false,
  activeDragId,
}: DataTableBodyV2Props<TData>) {
  const virtualRows = shouldVirtualize ? rowVirtualizer.getVirtualItems() : []
  const paddingTop = shouldVirtualize && virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom =
    shouldVirtualize && virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0

  if (isRefetching) {
    return <TableBodySkeleton nrOfColumns={nrOfColumns} />
  }

  return (
    <TableBody ref={tableBodyRef}>
      {rows.length > 0 ? (
        shouldVirtualize ? (
          // Virtualized rendering for large datasets
          <>
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} colSpan={nrOfColumns} />
              </tr>
            )}

            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index]
              return (
                <DataTableRowV2
                  key={row.id}
                  row={row}
                  virtualIndex={virtualRow.index}
                  frozenColOffsetsById={frozenColOffsetsById}
                  leafColumns={leafColumns}
                  freezePane={freezePane}
                  compactMode={compactMode}
                  cellMeasureRefs={cellMeasureRefs}
                  isRegrouping={isRegrouping}
                  activeDragId={activeDragId}
                />
              )
            })}

            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} colSpan={nrOfColumns} />
              </tr>
            )}
          </>
        ) : (
          // Non-virtualized rendering for small datasets
          rows.map((row) => (
            <DataTableRowV2
              key={row.id}
              row={row}
              frozenColOffsetsById={frozenColOffsetsById}
              leafColumns={leafColumns}
              freezePane={freezePane}
              compactMode={compactMode}
              cellMeasureRefs={cellMeasureRefs}
              isRegrouping={isRegrouping}
              activeDragId={activeDragId}
            />
          ))
        )
      ) : (
        <TableRow>
          <TableCell colSpan={nrOfColumns} className="h-24 text-center">
            No results.
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  )
}

interface DataTableRowV2Props<TData> {
  row: Row<TData>
  virtualIndex?: number
  frozenColOffsetsById: Record<string, number>
  leafColumns: Column<TData, unknown>[]
  freezePane?: number
  compactMode: boolean
  cellMeasureRefs: React.MutableRefObject<Record<string, HTMLDivElement[]>>
  isRegrouping?: boolean
  activeDragId?: string | null
  isOverlay?: boolean
  className?: string
}

export function DataTableRowV2<TData>({
  row,
  virtualIndex,
  frozenColOffsetsById,
  leafColumns,
  freezePane,
  compactMode,
  cellMeasureRefs,
  isRegrouping = false,
  activeDragId,
  isOverlay = false,
  className,
}: DataTableRowV2Props<TData>) {
  const isSubRow = row.depth > 0
  const rowData = row.original as Record<string, unknown>
  const rowId = String(rowData?.id || row.id)
  const isDragging = activeDragId === rowId

  const { setNodeRef, isOver } = useDroppable({
    id: rowId,
    disabled: !isRegrouping || isSubRow || isDragging || isOverlay, // Cannot drop on itself, subrows, or the overlay itself
  })

  return (
    <MotionTableRow
      ref={isOverlay ? undefined : setNodeRef}
      data-state={row.getIsSelected() && 'selected'}
      data-index={virtualIndex}
      data-row-id={rowId}
      data-depth={row.depth}
      className={cn(
        'odd:bg-offwhite/50 hover:bg-primary/5',
        {
          '!bg-secondary/10 hover:!bg-secondary/15': isSubRow,
          '!bg-secondary/30': !isSubRow && (row.getCanExpand() || !!rowData.isGroup),
          // Use pseudo-element for outline to ensure it's visible over sticky cells
          'after:border-1 relative after:pointer-events-none after:absolute after:inset-0 after:z-[50] after:border-primary after:bg-primary/40':
            isOver && !isDragging,
          'opacity-30': isDragging,
          // Ensure overlay has consistent background and no hover effects
          'bg-offwhite hover:bg-offwhite': isOverlay,
        },
        className
      )}
      initial={isSubRow ? { opacity: 0 } : undefined}
      animate={isSubRow ? { opacity: 1 } : undefined}
      transition={{ duration: 0.2, delay: 0.1 }}
    >
      {row.getVisibleCells().map((cell) => {
        const isActionCol = cell.column.id === 'actions' || cell.column.id === 'rowActions'
        const isExpanderCol = cell.column.id === 'expander'
        const isFrozen = freezePane && frozenColOffsetsById[cell.column.id] !== undefined
        const leftOffset = isFrozen ? frozenColOffsetsById[cell.column.id] : undefined
        const isLastFrozen = isFrozen && freezePane && leafColumns[freezePane - 1]?.id === cell.column.id

        return (
          <MotionTableCell
            layout
            className={clsx('border-b-[0.5px] last:border-b-[0.5px]', {
              'sticky right-0 z-[9] border-l': isActionCol,
              'bg-offwhite sticky z-[9] border-r': isFrozen || isActionCol,
              'drop-shadow-sm': isLastFrozen,
              'py-0.5': compactMode,
              // Add visual styling for sub-rows and group rows
              'bg-secondary/30 backdrop-blur':
                (isSubRow && (isExpanderCol || isFrozen)) ||
                (!isSubRow &&
                  (isExpanderCol || isFrozen || isActionCol) &&
                  (row.getCanExpand() || !!rowData.isGroup)) ||
                (isSubRow && isActionCol),
            })}
            style={
              isFrozen
                ? {
                    left: `${leftOffset}px`,
                  }
                : undefined
            }
            key={cell.id}
          >
            <div
              ref={(el) => {
                if (!isActionCol) {
                  const list = cellMeasureRefs.current[cell.column.id] || []
                  if (list.length === 0) {
                    cellMeasureRefs.current[cell.column.id] = list
                  }

                  if (!el) {
                    return
                  }

                  if (list.includes(el)) {
                    return
                  }

                  // Keep a unique set of measurement refs per column.
                  // Duplicate refs can cause noisy re-measurements and layout loops.
                  list.push(el)
                }
              }}
              className={clsx('min-h-6 items-center', {
                'flex justify-end': isActionCol,
                'flex h-full': !isActionCol,
                'pl-4': row.depth > 0 && !isExpanderCol && cell.column.id === 'title', // Indent only title column for subrows
              })}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          </MotionTableCell>
        )
      })}
    </MotionTableRow>
  )
}
